/**
 * PresentPresenter
 * --
 */
import React from 'react';
import { Spin, Button, Select, Input, Radio, Tooltip, Flex } from 'antd';
import {
  Content,
  Row,
  Col,
  BottomLayout,
  Image,
  Text,
  Modifier,
  Slider,
  HtmlContent,
} from 'components';
import { stringToMoneyFormat } from 'utils';
import { GiftCard, PresentCard } from './components';
import Formatter from 'utils/Formatter';

import Checked from 'assets/radio_checked.svg';
import UnChecked from 'assets/radio_unchecked.svg';
import { QuestionCircleOutlined } from '@ant-design/icons';

const PRESENT_TYPE = {
  PRODUCT: '배송상품',
  MENU: '교환권',
  CASH_VOUCHER: '금액권',
};

const PresentPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    data,
    cards,
    isLoading,
    type,
    onOptionChange,
    inputValue,
    onChange,
    paymentPrice,
    onPresent,
    barcode,
    cardRef,
    deliveryPrice,
    deliveryType,
    setDeliveryType,
    selectedCard,
    setSelectedCard,
    provision,
    temperatureOption,
    setTemperatureOption,
  } = props;

  /* ===== STATE ===== */
  const [isMessage, setIsMessage] = React.useState(true);
  const [selectedCategory, setSelectedCategory] = React.useState('전체');

  /* ===== VARIABLE ===== */
  const styles = {
    bottomButton: {
      width: '100%',
      marginBottom: 20,
    },

    wrap: {
      width: '100%',
      maxWidth: 425,
    },
    title: {
      fontSize: 16,
      fontWeight: 700,
      marginBottom: 10,
    },

    container: {
      borderRadius: 10,
    },
    flexRow: {
      display: 'flex',
    },
    flexCol: {
      display: 'flex',
      flexDirection: 'column',
    },

    imageWrap: {
      width: 80,
      height: 80,
    },

    optionWrap: {
      gap: 4,
      marginTop: 8,
    },

    input: {
      width: '100%',
      borderColor: '#DDDDDD',
    },

    relative: {
      position: 'relative',
      top: 0,
      left: 0,
    },
    absolute: {
      position: 'absolute',
    },
    removeButton: {
      width: 20,
      height: 20,
      borderRadius: '50%',
      top: -10,
      left: -10,
      border: 'none',
      background: '#BDBDBD',
      color: '#FFFFFF',
      cursor: 'pointer',
    },

    subTitle: {
      width: 80,
    },

    defaultRadio: {
      backgroundColor: '#FFFFFF',
      color: '#111111',
    },
    activeRadio: {
      backgroundColor: '#111111',
      color: '#FFFFFF',
    },

    defaultTempRadio: {
      border: '1px solid #BDBDBD',
    },
    activeTempHotRadio: {
      color: '#D33444',
      borderColor: '#D33444',
    },
    activeTempICERadio: {
      color: '#343AD3',
      borderColor: '#343AD3',
    },

    deliveryPrice: {
      fontSize: 12,
      color: '#999999',
    },

    categoryWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      height: 'auto',
    },
    category: {
      padding: '4px 12px',
      border: '1px solid #111111',
      borderRadius: 100,
      backgroundColor: '#FFFFFF',
      color: '#111111',
      cursor: 'pointer',
    },
    activeCategory: {
      backgroundColor: '#111111',
      color: '#FFFFFF',
    },

    cardWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: 24,
      flexWrap: 'nowrap',
      height: 'auto',
    },

    provision: {
      padding: 16,
      maxHeight: 100,
      overflowY: 'auto',
      background: '#F5F5F5',
    },
  };

  /* ===== FUNCTION ===== */
  /**
   * 옵션명
   * --
   * @param {array} option
   */
  const renderOptionName = (option) => {
    if (!option || !option.length) return '';

    const parseOption = option.filter((op) => op).map((op) => JSON.parse(op));

    return parseOption
      .map((cur) => cur.option.map((op) => op.option_name).join(' / '))
      .join(' / ');
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content
          maxWidth={'100%'}
          minHeight={'100vh'}
          padding={12}
          backgroundColor="#F5F5F5"
          style={{ ...styles.relative, paddingBottom: 150 }}
        >
          <Row gutter={[12, 12]}>
            {/* 상품정보 */}
            <Col x={24}>
              <Content maxWidth={'100%'} style={{ ...styles.container }}>
                <div style={{ ...styles.wrap }}>
                  <Text style={{ ...styles.title }}>상품정보</Text>
                  <div
                    style={{ ...styles.container, ...styles.flexRow, gap: 12 }}
                  >
                    <div style={{ ...styles.imageWrap }}>
                      <Image
                        src={data?.thumbnail}
                        alt={data?.[`${type}_name`]}
                        width={'100%'}
                        height={'100%'}
                      />
                    </div>

                    <div
                      style={{
                        ...styles.flexCol,
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <Text size={14} color={'var(--dark-gray-color)'}>
                          {data?.shop_information_name}
                        </Text>
                        <Text weight={700}>{data?.[`${type}_name`]}</Text>
                      </div>
                      <Text>{stringToMoneyFormat(data?.price, '원')}</Text>
                    </div>
                  </div>
                </div>

                {/* 배송, 스토어픽업 */}
                {type === 'product' ? (
                  <div style={{ ...styles.flexCol, ...styles.optionWrap }}>
                    <Radio.Group
                      block
                      value={deliveryType}
                      onChange={(e) => setDeliveryType(e.target.value)}
                    >
                      <Radio.Button
                        value={'DELIVERY'}
                        style={
                          deliveryType === 'DELIVERY'
                            ? styles.activeRadio
                            : styles.defaultRadio
                        }
                      >
                        배송
                      </Radio.Button>
                      {data?.is_store_pickup === 'Y' ? (
                        <Radio.Button
                          value={'STORE_PICKUP'}
                          style={
                            deliveryType === 'STORE_PICKUP'
                              ? styles.activeRadio
                              : styles.defaultRadio
                          }
                        >
                          스토어픽업
                        </Radio.Button>
                      ) : null}
                    </Radio.Group>
                  </div>
                ) : type === 'menu' && data?.temperature_option !== 'OTHER' ? (
                  <div style={{ ...styles.flexCol, ...styles.optionWrap }}>
                    <Radio.Group
                      block
                      value={temperatureOption}
                      onChange={(e) => setTemperatureOption(e.target.value)}
                    >
                      {data?.temperature_option !== 'ICE' ? (
                        <Radio.Button
                          value={'HOT'}
                          style={
                            temperatureOption === 'HOT'
                              ? styles.activeTempHotRadio
                              : styles.defaultTempRadio
                          }
                        >
                          HOT
                        </Radio.Button>
                      ) : null}
                      {data?.temperature_option !== 'HOT' ? (
                        <Radio.Button
                          value={'ICE'}
                          style={
                            temperatureOption === 'ICE'
                              ? styles.activeTempICERadio
                              : styles.defaultTempRadio
                          }
                        >
                          ICE
                        </Radio.Button>
                      ) : null}
                    </Radio.Group>
                  </div>
                ) : null}

                {type === 'product' ? (
                  <Text size={12} maxLine={2} color={'#D33444'}>
                    * {data?.present_information?.present_expired_date}일 이내{' '}
                    {deliveryType === 'DELIVERY' ? '배송지' : '픽업장소'}를
                    입력하지 않을 경우 00시 자동 취소됩니다.
                  </Text>
                ) : null}

                {/* 상품 필수 옵션 */}
                {type === 'product' && data?.product_option.length ? (
                  <div style={{ ...styles.flexCol, ...styles.optionWrap }}>
                    {data.product_option
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map((option, index) => (
                        <Select
                          key={`required-${option.option_id}-${index}`}
                          placeholder={option.option_title}
                          size="large"
                          value={data.selected_option[index]}
                          options={option.option.map((child) => ({
                            key: `${option.option_id}-${child.option_key}`,
                            label: `${child.option_name}${
                              child.option_price
                                ? ` (+${child.option_price})`
                                : ''
                            }`,
                            value: JSON.stringify({
                              ...option,
                              option: [child],
                            }),
                          }))}
                          onChange={(e) => onOptionChange(e, index)}
                        />
                      ))}
                  </div>
                ) : null}
              </Content>
            </Col>

            {/* 상품정보 */}
            <Col x={24}>
              <Content maxWidth={'100%'} style={{ ...styles.container }}>
                <div style={{ ...styles.wrap }}>
                  <Text style={{ ...styles.title }}>선물정보</Text>
                  <div
                    style={{ ...styles.container, ...styles.flexCol, gap: 4 }}
                  >
                    <div style={{ ...styles.flexRow, gap: 8 }}>
                      <Text
                        size={14}
                        weight={500}
                        style={{ ...styles.subTitle }}
                      >
                        교환권 타입
                      </Text>

                      <Text size={14}>
                        {PRESENT_TYPE[type.toLocaleUpperCase()]}
                      </Text>
                    </div>

                    <div style={{ ...styles.flexRow, gap: 8 }}>
                      <Text
                        size={14}
                        weight={500}
                        style={{ ...styles.subTitle }}
                      >
                        유효기간
                      </Text>

                      <Text size={14}>
                        {data?.present_information?.present_expired_date}일
                      </Text>
                    </div>
                  </div>
                </div>
              </Content>
            </Col>

            {/* 받는사람 */}
            <Col x={24}>
              <Content maxWidth={'100%'} style={{ ...styles.container }}>
                <div style={{ ...styles.wrap }}>
                  <Text style={{ ...styles.title }}>받는사람</Text>
                  <div
                    style={{ ...styles.container, ...styles.flexRow, gap: 12 }}
                  >
                    <div
                      style={{
                        ...styles.flexCol,
                        ...styles.optionWrap,
                        flex: 1,
                      }}
                    >
                      <Input
                        size="large"
                        placeholder="이름"
                        value={inputValue?.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        maxLength={5}
                        style={{ ...styles.input }}
                      />
                      <Input
                        size="large"
                        placeholder="연락처"
                        value={inputValue?.phone}
                        onChange={(e) =>
                          onChange('phone', Formatter.hyphen(e.target.value))
                        }
                        maxLength={13}
                        style={{ ...styles.input }}
                      />
                    </div>
                  </div>
                </div>
              </Content>
            </Col>

            {/* 카드/메세지 */}
            <Col x={24}>
              <Content maxWidth={'100%'} style={{ ...styles.container }}>
                <div style={{ ...styles.wrap }}>
                  <Text style={{ ...styles.title }}>메세지</Text>
                  <div
                    style={{
                      ...styles.container,
                      ...styles.flexRow,
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        ...styles.flexCol,
                        ...styles.optionWrap,
                        flex: 1,
                        width: '100%',
                      }}
                    >
                      {cards ? (
                        <div>
                          <Slider
                            items={Object.keys(cards)}
                            slidesPerView={'auto'}
                            spaceBetween={8}
                            render={(item) => (
                              <div
                                style={{
                                  ...styles.category,
                                  ...(selectedCategory === item
                                    ? styles.activeCategory
                                    : {}),
                                }}
                                onClick={() => setSelectedCategory(item)}
                              >
                                {item}
                              </div>
                            )}
                            itemStyle={{ width: 'auto' }}
                          />

                          <Slider
                            items={cards[selectedCategory]}
                            render={(item) => (
                              <GiftCard
                                key={item.present_card_id}
                                title={item.title}
                                url={item.image_url}
                                onClick={() =>
                                  setSelectedCard(item.present_card_id)
                                }
                                active={item.present_card_id === selectedCard}
                              />
                            )}
                            style={{ ...styles.cardWrap }}
                            itemStyle={{ width: '70%', cursor: 'pointer' }}
                            slidesPerView={'auto'}
                            spaceBetween={12}
                          />
                        </div>
                      ) : null}

                      {isMessage ? (
                        <div style={{ ...styles.relative }}>
                          <Input.TextArea
                            size="large"
                            placeholder="선물과 함께 보낼 메시지를 적어보세요."
                            value={inputValue?.message}
                            onChange={(e) =>
                              onChange('message', e.target.value)
                            }
                            style={{
                              ...styles.input,
                              resize: 'none',
                              height: 120,
                            }}
                            showCount
                            count
                            maxLength={100}
                          />

                          <button
                            style={{
                              ...styles.absolute,
                              ...styles.removeButton,
                            }}
                            onClick={() => setIsMessage(!isMessage)}
                          >
                            x
                          </button>

                          <Text
                            size={10}
                            color={'#900020'}
                            style={{ marginTop: 4 }}
                          >
                            특수문자 또는 이모지 삽입 시 문자 발송이 어려울 수
                            있습니다.
                          </Text>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Content>
            </Col>

            {/* 동의 */}
            <Col x={24}>
              <Content maxWidth={'100%'} style={{ ...styles.container }}>
                <div style={{ ...styles.wrap }}>
                  <div style={{ ...styles.provision }}>
                    <HtmlContent
                      content={provision?.provision_content ?? '-'}
                    />
                  </div>
                  <div
                    style={{
                      ...styles.flexRow,
                      gap: 8,
                      cursor: 'pointer',
                      marginTop: 8,
                    }}
                    onClick={() => onChange('agree', !inputValue?.agree)}
                  >
                    <img
                      src={inputValue?.agree ? Checked : UnChecked}
                      width={15}
                      alt="checked"
                    />

                    <Text
                      size={14}
                      color={'#666666'}
                      maxLine={2}
                      style={{ cursor: 'pointer' }}
                    >
                      주문내용 및 결제조건을 확인했으며, 결제진행에 동의합니다.
                    </Text>
                  </div>
                </div>
              </Content>
            </Col>
          </Row>

          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              ...styles.absolute,
              top: 0,
              left: 0,
              opacity: 0,
              zIndex: -1,
              // opacity: 1,
              // zIndex: 99999,
            }}
          >
            <PresentCard
              cardRef={cardRef}
              thumbnail={data?.thumbnail}
              type={type}
              brand={data?.shop_information_name}
              barcode={barcode?.barcode}
              barcodeImage={barcode?.url}
              product={`${data?.[`${type}_name`]}${
                type === 'menu' && data?.temperature_option !== 'OTHER'
                  ? ` (${temperatureOption})`
                  : ''
              }`}
              option={renderOptionName(data?.selected_option)}
            />
          </Content>
        </Content>
      </Spin>

      <BottomLayout shadow>
        <div
          style={{
            ...styles.flexRow,
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <Flex vertical>
            <Text size={18} weight={500}>
              결제금액{' '}
            </Text>
            {deliveryType === 'DELIVERY' && deliveryPrice ? (
              <span style={{ ...styles.deliveryPrice }}>
                (배송비 +{stringToMoneyFormat(deliveryPrice, '원')}){' '}
                <Tooltip
                  title={`${stringToMoneyFormat(
                    data?.free_delivery_price,
                    '원'
                  )} 이상 구매시 무료배송`}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            ) : null}
          </Flex>

          <div style={{ ...styles.flexRow, gap: 32 }}>
            <Modifier
              minNum={1}
              maxNum={5}
              unit={null}
              init={inputValue?.amount}
              onIncrease={() => onChange('amount', ++inputValue.amount)}
              onDecrease={() => onChange('amount', --inputValue.amount)}
              layoutStyle={{
                width: 100,
                height: 32,
              }}
              readOnly
            />
            <Text size={24} weight={700}>
              {stringToMoneyFormat(paymentPrice + deliveryPrice, '원')}
            </Text>
          </div>
        </div>
        <Button
          type="primary"
          size="large"
          style={{ ...styles.bottomButton }}
          onClick={() => onPresent()}
        >
          선물하기
        </Button>
      </BottomLayout>
    </>
  );
};

export default PresentPresenter;
